import type { ComponentProps, ElementType } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { space, themeGet } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { BodyRegularBR2 } from 'common/components/Typography';
import { breakpoint } from 'common/theme/helper';
import type { SpaceProps } from 'common/theme/system-types';
import TrustSealsItemIcon from './TrustSealsItemIcon';
import type { TrustSeal, TrustSealIcon, TrustSealLink } from './types';

const Container = styled(Flex)`
  & + & {
    ${breakpoint(1)} {
      margin-left: ${themeGet('space.sp_24')}px;
    }
    ${breakpoint(2)} {
      margin-left: ${themeGet('space.sp_32')}px;
    }
  }
`;

const containerStyles: ComponentProps<typeof Container> = {
  flexDirection: ['column', null, 'row'],
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'inherit',
  width: [null, 1],
  mb: ['sp_16', 'sp_20', 'sp_0'],
  px: ['sp_8', 'sp_12', 'sp_0'],
  textAlign: ['center', null, 'left'],
};

const IconWrapper = styled.span<SpaceProps>`
  ${space};
`;

const Text = styled(BodyRegularBR2)`
  && p {
    margin: ${themeGet('space.sp_0')};
  }
`;

const getLinkProps = (link: TrustSealLink, icon: Nullable<TrustSealIcon>) => ({
  as: 'a' as ElementType,
  href: link.url,
  target: link.target,
  'aria-label': icon?.alt ?? undefined,
  rel: 'noreferrer',
});

const TrustSealsItem = ({ link, icon, text }: TrustSeal) => {
  const iconWrapperProps = link?.url ? getLinkProps(link, icon) : null;

  return (
    <Container {...containerStyles}>
      {icon && (
        <IconWrapper mr="sp_8" {...iconWrapperProps}>
          <TrustSealsItemIcon url={icon.url} alt={icon.alt} />
        </IconWrapper>
      )}
      {text && <Text dangerouslySetInnerHTML={{ __html: text }} />}
    </Container>
  );
};

export default TrustSealsItem;
